<template>

    <ui-modal
        class="confirm-delete-action-modal"
        title="Weet je zeker dat je deze actie wilt verwijderen?"
        @close="cancel"
        v-spinner="loading">

        <div class="modal-content">
            <!-- Current Action Table -->
            <ui-table>
                <thead>
                    <ui-table-header>
                        <th>Actie</th>
                        <th>Bruto</th>
                        <th>Netto</th>
                        <th>Tarra</th>
                        <th>Bustal</th>
                        <th>Stukstal</th>
                    </ui-table-header>
                </thead>
                <tbody>
                    <ui-tr v-if="action">
                        <td>{{ actionDisplayName }} ({{ action.date | date("DD-MM-YYYY") }})</td>
                        <td>{{ formatWeight(action.brutoWeight) }}</td>
                        <td>{{ formatWeight(action.nettoWeight) }}</td>
                        <td>{{ formatPercentage(action.tarra) }}</td>
                        <td>{{ formatNumber(action.bustal) }}</td>
                        <td>{{ formatNumber(action.stukstal) }}</td>
                    </ui-tr>
                </tbody>
            </ui-table>

            <!-- Warning Section -->
            <template v-if="deletableActions && deletableActions.length > 0">

                <!-- Subsequent Actions Table -->
                <ui-table>
                    <thead>
                        <ui-table-header>
                            <ui-th class="warning-header" colspan="100%">
                                <div class="warning-content">
                                    <svg
                                        class="warning-icon"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        fill="currentColor"
                                        viewBox="0 0 256 256"><path d="M236.8,188.09,149.35,36.22h0a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"/></svg>
                                    <span class="warning-text">Let op! Alle acties die na deze actie zijn uitgevoerd worden ook verwijderd:</span>
                                </div>
                            </ui-th>
                        </ui-table-header>
                        <ui-table-header>
                            <ui-th>Actie</ui-th>
                            <ui-th>Bruto</ui-th>
                            <ui-th>Netto</ui-th>
                            <ui-th>Tarra</ui-th>
                            <ui-th>Bustal</ui-th>
                            <ui-th>Stukstal</ui-th>
                        </ui-table-header>
                    </thead>
                    <tbody>
                        <ui-tr v-for="subsequentAction in deletableActions" :key="`${subsequentAction.__typename}_${subsequentAction.id}`">
                            <td>{{ subsequentAction.__typename | actionTypeTitle(subsequentAction) }} ({{ subsequentAction.date | date("DD-MM-YYYY") }})</td>
                            <td>{{ formatWeight(subsequentAction.brutoWeight) }}</td>
                            <td>{{ formatWeight(subsequentAction.nettoWeight) }}</td>
                            <td>{{ formatPercentage(subsequentAction.tarra) }}</td>
                            <td>{{ formatNumber(subsequentAction.bustal) }}</td>
                            <td>{{ formatNumber(subsequentAction.stukstal) }}</td>
                        </ui-tr>
                    </tbody>
                </ui-table>
            </template>

            <!-- Action Buttons -->
            <div class="modal-actions">
                <ui-button @click="cancel">Annuleren</ui-button>
                <ui-button color="negative" @click="confirmDelete">
                    {{ deleteButtonText }}
                </ui-button>
            </div>
        </div>

    </ui-modal>

</template>

<script>

import Vue from 'vue';
import { sortBy } from 'es-toolkit/compat';
import Component from 'vue-class-component';

import dismissalMixin from 'js-core/js/mixins/dismissal';
import createQuery from 'js-core/js/apollo/queries/createQuery';

import GetActionsAfterDataQuery from 'gql/queries/ConfirmDeleteActionModal/GetActionsAfterData.gql';

@Component({

    name: 'ConfirmDeleteActionModal',
    mixins: [dismissalMixin],
    props: ['action'],
    apollo: {
        deletableActions: createQuery(GetActionsAfterDataQuery, {

            skip() {
                return !this.action || !this.actionId || !this.actionType;
            },

            variables() {
                return {
                    referenceActionId: this.actionId,
                    referenceActionType: this.actionType
                };
            }
        })
    }
})
export default class ConfirmDeleteActionModal extends Vue {

    data() {

        return {

            loading: 0
        };
    }

    get resolvedDeletableActions() {

        // Ensure deletableActions is always an array
        return sortBy(this.deletableActions || [], 'date');
    }

    get actionId() {

        return this.action ? this.action.id : null;
    }

    get actionType() {

        return this.action ? this.action.__typename : null;
    }

    get actionDisplayName() {

        if (!this.action) return '';

        // Use the actionTypeTitle filter to get the proper Dutch name
        return this.$options.filters.actionTypeTitle(this.action.__typename, this.action);
    }

    get deleteButtonText() {

        const subsequentCount = this.deletableActions ? this.deletableActions.length : 0;

        if (subsequentCount === 0) {
            return 'Verwijder actie';
        }
        if (subsequentCount === 1) {
            return 'Verwijder actie incl. 1 andere actie';
        }
        return `Verwijder actie incl. ${subsequentCount} andere acties`;
    }

    confirmDelete() {

        // Emit event to parent component to handle the actual deletion
        this.$emit('dismiss', true);
    }

    formatWeight(value) {
        if (!value && value !== 0) return '-';
        return `${this.$options.filters.number(value)}mTon`;
    }

    formatPercentage(value) {
        if (!value && value !== 0) return '-';
        return `${this.$options.filters.number(value)}%`;
    }

    formatNumber(value) {
        if (!value && value !== 0) return '-';
        return this.$options.filters.number(value);
    }
}

</script>